import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import dateDiffToDate from 'helper/dateDiffToDate';
import formatDateDifference from 'helper/formatDateDifference';
import sortedDatesAsIntervals from 'helper/Shared/sortedDatesAsIntervals';
import defaultModelDate from 'helper/Meteorology/Fields/ClassicComparator/modelDateDefaults';
import { selectMFClassicComparatorVariable } from 'redux/reducers/meteorologyFieldsClassicComparatorControlPanelReducer';
import useAvailableDates from 'utils/hooks/meteorology/comparator/useAvailableDates';
import useControlPanelOptions from 'utils/hooks/meteorology/useControlPanelOptions';
import useClassicComparatorDates from './useClassicComparatorDates';

export interface UseClassicComparatorModelParams {
  model: string;
  setModel: (value: string) => void;
  dateDiff: string;
  setDateDiff: (value: string | undefined) => void;
  runtime: number | undefined;
  setRuntime: (value: number | undefined) => void;
  member: string | undefined;
  setMember: (value: string | undefined) => void;
  isBaseModel: boolean;
}

/**
 * Opções derivadas e normalização de um modelo (base ou confrontante) do Comparador Clássico.
 *
 * Concentra as regras que antes viviam dentro do `ModelAccordion` do desktop, para que a
 * versão mobile use exatamente as mesmas sem duplicá-las:
 * - modelos disponíveis dependem da variável selecionada;
 * - trocar o modelo reseta data/runtime/membro e o período de análise;
 * - runtime e membro se auto-selecionam (e se limpam quando saem da lista);
 * - o estado guarda diferença de dias (`'d+1'`), então a conversão data↔diff mora aqui.
 */
export default function useClassicComparatorModel({
  model,
  setModel,
  dateDiff,
  setDateDiff,
  runtime,
  setRuntime,
  member,
  setMember,
  isBaseModel,
}: UseClassicComparatorModelParams) {
  const { resetPeriodDates } = useClassicComparatorDates();
  const variable = useSelector(selectMFClassicComparatorVariable);
  const { getModelOptions, getModelsForVariable, isFetching } = useControlPanelOptions('comparador');

  const modelOptions = useMemo(
    () => getModelsForVariable(variable),
    [variable, getModelsForVariable],
  );
  const availableDates = useAvailableDates(model);
  const availableIntervals = useMemo(
    () => sortedDatesAsIntervals(availableDates),
    [availableDates],
  );
  const {
    runtimes: runtimeOptions,
    members: memberOptions,
  } = useMemo(() => getModelOptions(model), [model, getModelOptions]);

  const date = useMemo(
    () => dateDiffToDate(dateDiff, availableDates, false),
    [dateDiff, availableDates],
  );

  const resetModelDependencies = useCallback(() => {
    setDateDiff(undefined);
    setRuntime(undefined);
    setMember(undefined);
  }, [setDateDiff, setRuntime, setMember]);

  const setDate = useCallback((value: Date | null) => {
    const newDiff = formatDateDifference(value, availableDates, false);
    if (newDiff !== dateDiff) {
      resetModelDependencies();
      setDateDiff(newDiff);
    }
  }, [availableDates, dateDiff, resetModelDependencies, setDateDiff]);

  const selectModel = useCallback((value: string) => {
    if (model !== value) {
      resetPeriodDates();
      resetModelDependencies();
    }
    setModel(value);
  }, [model, setModel, resetPeriodDates, resetModelDependencies]);

  useEffect(() => {
    const runtimeIsAvailable = runtimeOptions.some((option) => option.id === runtime);
    if (!isFetching && !runtimeIsAvailable) {
      setRuntime(undefined);
    }
    if (runtimeOptions.length >= 1 && runtime === undefined) {
      setRuntime(runtimeOptions[0].id);
    }
  }, [isFetching, runtime, runtimeOptions, setRuntime]);

  useEffect(() => {
    const memberIsAvailable = memberOptions.some((option) => option.id === member);
    if (!isFetching && !memberIsAvailable) {
      setMember(undefined);
    }
    if (memberOptions.length >= 1 && member === undefined) {
      setMember(memberOptions[0].id);
    }
  }, [isFetching, member, memberOptions, setMember]);

  // Data de previsão default ao abrir (#1822, item 5.3 — regra da Bruna): o
  // confrontante abre na data mais recente; o base, na penúltima (compara a
  // rodada anterior com a mais recente). `defaultModelDate` encapsula a regra.
  useEffect(() => {
    if (dateDiff === undefined && availableDates.length > 0) {
      const defaultDate = defaultModelDate(availableDates, isBaseModel);
      setDateDiff(formatDateDifference(defaultDate ?? null, availableDates, false));
    }
  }, [dateDiff, availableDates, setDateDiff, isBaseModel]);

  return {
    isFetching,
    modelOptions,
    availableDates,
    availableIntervals,
    runtimeOptions,
    memberOptions,
    date,
    setDate,
    selectModel,
  };
}
